import { useCallback } from 'react';
import { FlatList, Image, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { copyExcerciseSet } from '@/api/services/user';
import avatars from '@/assets/avatars';
import colors, { appTheme } from '@/config/colors';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { styles } from './styles';
import { Patient } from '@/stores/store.types';

type CopyModalProps = {
  hideCopyModal: () => void;
  infoCopyModal: {
    title: string;
    idOfDataSet: string;
    type: string;
  };
  localSelectedPatientID: string;
  refreshPatient: () => void;
  setInfoCopyModal: (value: {
    title: string;
    idOfDataSet: string;
    type: string;
  }) => void;
  setLocalSelectedPatientID: (id: string) => void;
  setShowCopyErrorModal: (value: boolean) => void;
  setShowCopyModal: (value: boolean) => void;
  showCopyModal: boolean;
};

export const CopyModal = ({
  hideCopyModal,
  infoCopyModal,
  localSelectedPatientID,
  refreshPatient,
  setInfoCopyModal,
  setLocalSelectedPatientID,
  setShowCopyErrorModal,
  setShowCopyModal,
  showCopyModal,
}: CopyModalProps) => {
  const { showNotification } = useNotification();
  const {
    patients,
    changeID,
    toggleRefreshOnCopySets,
    setAllowefreshOnCopySets,
  } = useDashboardContext();

  const copyPatient = useCallback(
    async ({
      userId,
      setId,
      type,
    }: {
      userId: string;
      setId: string;
      type: string;
    }) => {
      try {
        let set = '';
        let setTitle = '';
        if (type === 'Sätzesets') {
          set = 'sentencesets';
          setTitle = 'Sentenceset';
        } else if (type === 'Wörtersets') {
          set = 'wordsets';
          setTitle = 'Wordset';
        }
        const res = await copyExcerciseSet(userId, setId, set, setTitle);
        if (
          res?.data?.result === 'ok' ||
          res?.data?.message?.includes('success copy to')
        ) {
          setShowCopyModal(false);
          setInfoCopyModal({ title: '', idOfDataSet: '', type: '' });
          changeID(userId, true, patients as Patient[] | undefined);
          setAllowefreshOnCopySets(true);
          toggleRefreshOnCopySets();
          const patient = (patients ?? []).find((p) => p._id === userId);
          refreshPatient();
          showNotification({
            type: 'success',
            title: `Set erfolgreich an ${patient?.firstName} ${patient?.lastName} kopiert`,
          });
        } else {
          throw new Error('');
        }
      } catch (_error) {
        setShowCopyErrorModal(true);
      }
    },
    [],
  );

  return (
    <Modal
      customStyle={{ maxHeight: 500, flex: 1 }}
      isVisible={showCopyModal}
      onClose={hideCopyModal}
      cancelActionButton={
        <Button label="Abbrechen" onPress={hideCopyModal} look="secondary" />
      }
      confirmActionButton={
        <Button
          label="Kopieren"
          onPress={() =>
            copyPatient({
              userId: localSelectedPatientID,
              setId: infoCopyModal.idOfDataSet,
              type: infoCopyModal.type,
            })
          }
        />
      }
      title={`${infoCopyModal.title} kopieren`}
    >
      <View style={styles.maxWidth}>
        <View style={styles.underline} />
        <View
          style={{
            paddingHorizontal: 10,
            flex: 1,
            maxHeight: '90%',
            minHeight: (patients ?? []).length > 3 ? 200 : 150,
          }}
        >
          <Text style={styles.titleTwo}>
            Zu welchem Patienten-Profil möchten Sie das Übungsset kopieren?
          </Text>
          <FlatList
            data={patients}
            keyExtractor={(item) => item._id}
            numColumns={3}
            renderItem={({ item }) => {
              const isSelected = localSelectedPatientID == item._id;
              const name = item.firstName + ' ' + item.lastName;
              const imageFromDB = item.avatar[0]
                .split('img')[1]
                .split('/')[2]
                .split('.')[0];
              const image = avatars[imageFromDB as keyof typeof avatars];
              const idStyles = StyleSheet.create({
                backgroundColor: {
                  backgroundColor: isSelected ? appTheme.primary : colors.white,
                  borderColor: isSelected ? 'transparent' : colors.black,
                  borderWidth: 1,
                },
              });
              return (
                <PressableNeolexon
                  style={[styles.listItem]}
                  onPress={() => setLocalSelectedPatientID(item._id)}
                >
                  <View style={[styles.userBox, idStyles.backgroundColor]}>
                    <Image style={styles.userImg} source={image} />
                    <Text
                      numberOfLines={2}
                      ellipsizeMode="tail"
                      style={{
                        color: isSelected ? colors.white : colors.black,
                        maxWidth: '75%',
                      }}
                    >
                      {name}
                    </Text>
                  </View>
                </PressableNeolexon>
              );
            }}
          />
        </View>
      </View>
    </Modal>
  );
};
